import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, NgZone, ChangeDetectorRef, inject, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Blog, BlogRevision, BrandVoiceScoreDisplay } from '../../types';
import { RunView } from '@memberjunction/core';
import { marked } from 'marked';

interface EditDraft {
  Title: string;
  Content: string;
  Author: string;
  Tags: string;
  URLSlug: string;
  MetaDescription: string;
}

interface CritiqueType {
  key: string;
  label: string;
  icon: string;
  description: string;
  requiresFeedback: boolean;
}

@Component({
  standalone: false,
  selector: 'mkt-blog-detail-panel',
  template: `
    <div class="mkt-detail-panel">
      <!-- Header -->
      <div class="mkt-detail-panel__header">
        <input *ngIf="editing && editDraft" class="mkt-detail-panel__title-input"
               [(ngModel)]="editDraft.Title" placeholder="Blog title" />
        <h3 *ngIf="!editing" class="mkt-detail-panel__title">{{ blog.Title }}</h3>
        <div class="mkt-detail-panel__header-actions">
          <button *ngIf="!editing && revisionCount > 0" class="mkt-detail-panel__history-btn"
                  (click)="toggleHistory()" title="Revision history"
                  [class.mkt-detail-panel__history-btn--active]="showHistory">
            <i class="fa-solid fa-clock-rotate-left"></i>
            <span class="mkt-detail-panel__history-count">{{ revisionCount }}</span>
          </button>
          <button *ngIf="!editing" class="mkt-detail-panel__edit-btn" (click)="enterEdit()" title="Edit">
            <i class="fa-solid fa-pencil"></i>
          </button>
          <button class="mkt-detail-panel__close" (click)="close.emit()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <!-- Brand Voice Score Banner -->
      <div *ngIf="brandVoiceScore" class="mkt-detail-panel__score-banner"
           [class.score-green]="brandVoiceScore.overall >= 80"
           [class.score-yellow]="brandVoiceScore.overall >= 60 && brandVoiceScore.overall < 80"
           [class.score-red]="brandVoiceScore.overall < 60">
        <div class="mkt-detail-panel__score-header">
          <i class="fa-solid fa-shield-halved"></i>
          <span class="mkt-detail-panel__score-value">{{ brandVoiceScore.overall }}/100</span>
          <span class="mkt-detail-panel__score-label">Brand Voice Score</span>
        </div>
        <div class="mkt-detail-panel__score-dimensions" *ngIf="showScoreDetails">
          <div *ngFor="let dim of scoreDimensions" class="mkt-detail-panel__score-dim">
            <span class="mkt-detail-panel__score-dim-name">{{ dim.label }}</span>
            <span class="mkt-detail-panel__score-dim-value">{{ dim.score }}/100</span>
            <div class="mkt-detail-panel__score-dim-bar">
              <div class="mkt-detail-panel__score-dim-fill" [style.width.%]="dim.score"></div>
            </div>
          </div>
        </div>
        <div class="mkt-detail-panel__score-flags" *ngIf="showScoreDetails && brandVoiceScore.flags.length">
          <div *ngFor="let flag of brandVoiceScore.flags" class="mkt-detail-panel__score-flag">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <span>"{{ flag.passage }}" — {{ flag.issue }}</span>
          </div>
        </div>
        <button class="mkt-detail-panel__score-toggle" (click)="showScoreDetails = !showScoreDetails">
          {{ showScoreDetails ? 'Hide details' : 'Show details' }}
          <i [class]="showScoreDetails ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="mkt-detail-panel__body">
        <!-- History View -->
        <ng-container *ngIf="showHistory; else normalView">
          <!-- Viewing a single revision -->
          <ng-container *ngIf="selectedRevision; else revisionList">
            <div class="mkt-detail-panel__history-nav">
              <button class="mkt-detail-panel__back-btn" (click)="selectedRevision = null">
                <i class="fa-solid fa-arrow-left"></i> Back to list
              </button>
              <span class="mkt-detail-panel__revision-tag">Revision #{{ selectedRevision.RevisionNumber }}</span>
            </div>
            <div *ngIf="selectedRevision.Feedback" class="mkt-detail-panel__revision-feedback-display">
              <span *ngIf="getCritiqueBadge(selectedRevision.Feedback)" class="mkt-detail-panel__critique-badge">
                <i [class]="'fa-solid ' + getCritiqueBadge(selectedRevision.Feedback)!.icon"></i> {{ getCritiqueBadge(selectedRevision.Feedback)!.label }}
              </span>
              <ng-container *ngIf="getFeedbackText(selectedRevision.Feedback) as text">
                <i class="fa-solid fa-comment-dots"></i> {{ text }}
              </ng-container>
            </div>
            <div class="mkt-detail-panel__content" [innerHTML]="selectedRevisionContent"></div>
          </ng-container>

          <!-- Revision list -->
          <ng-template #revisionList>
            <div class="mkt-detail-panel__history-nav">
              <button class="mkt-detail-panel__back-btn" (click)="showHistory = false">
                <i class="fa-solid fa-arrow-left"></i> Back to current
              </button>
              <span class="mkt-detail-panel__history-title">Revision History</span>
            </div>
            <div *ngIf="loadingHistory" class="mkt-detail-panel__history-loading">
              <i class="fa-solid fa-spinner fa-spin"></i> Loading revisions...
            </div>
            <div *ngFor="let rev of revisions" class="mkt-detail-panel__revision-card"
                 (click)="viewRevision(rev)">
              <div class="mkt-detail-panel__revision-card-header">
                <span class="mkt-detail-panel__revision-num">#{{ rev.RevisionNumber }}</span>
                <span class="mkt-detail-panel__revision-date">{{ rev.__mj_CreatedAt | date:'short' }}</span>
              </div>
              <div *ngIf="rev.Title" class="mkt-detail-panel__revision-card-title">{{ rev.Title }}</div>
              <div *ngIf="rev.Feedback" class="mkt-detail-panel__revision-card-feedback">
                <span *ngIf="getCritiqueBadge(rev.Feedback)" class="mkt-detail-panel__critique-badge">
                  <i [class]="'fa-solid ' + getCritiqueBadge(rev.Feedback)!.icon"></i> {{ getCritiqueBadge(rev.Feedback)!.label }}
                </span>
                <ng-container *ngIf="getFeedbackText(rev.Feedback) as text">
                  <i class="fa-solid fa-comment-dots"></i> {{ text | slice:0:120 }}{{ text.length > 120 ? '...' : '' }}
                </ng-container>
              </div>
            </div>
          </ng-template>
        </ng-container>

        <!-- Normal view -->
        <ng-template #normalView>
        <mkt-status-badge [status]="blog.Status"></mkt-status-badge>

        <!-- Featured Image -->
        <img *ngIf="blog.FeaturedImageURL && !editing" [src]="blog.FeaturedImageURL"
             class="mkt-detail-panel__featured-img" alt="Featured image" />

        <!-- Rendered Markdown (view mode) -->
        <div *ngIf="!editing" class="mkt-detail-panel__content" [innerHTML]="renderedContent"></div>

        <!-- Raw Markdown (edit mode) -->
        <textarea *ngIf="editing && editDraft" class="mkt-detail-panel__content-edit"
                  [(ngModel)]="editDraft.Content" placeholder="Blog content (markdown)"></textarea>

        <!-- SEO Metadata -->
        <div class="mkt-detail-panel__section">
          <h4 class="mkt-detail-panel__section-title" (click)="showSeo = !showSeo">
            <i class="fa-solid fa-magnifying-glass-chart"></i> SEO Metadata
            <i class="fa-solid" [ngClass]="showSeo ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </h4>
          <div *ngIf="showSeo || editing" class="mkt-detail-panel__seo">
            <div class="mkt-detail-panel__seo-row">
              <label>Author</label>
              <input *ngIf="editing && editDraft" class="mkt-detail-panel__seo-input"
                     [(ngModel)]="editDraft.Author" placeholder="Author name" />
              <span *ngIf="!editing">{{ blog.Author || '—' }}</span>
            </div>
            <div class="mkt-detail-panel__seo-row">
              <label>Slug</label>
              <input *ngIf="editing && editDraft" class="mkt-detail-panel__seo-input"
                     [(ngModel)]="editDraft.URLSlug" placeholder="url-slug" />
              <span *ngIf="!editing">{{ blog.URLSlug || '—' }}</span>
            </div>
            <div class="mkt-detail-panel__seo-row">
              <label>Meta Description</label>
              <textarea *ngIf="editing && editDraft" class="mkt-detail-panel__seo-textarea"
                        [(ngModel)]="editDraft.MetaDescription" placeholder="Meta description"></textarea>
              <span *ngIf="!editing">{{ blog.MetaDescription || '—' }}</span>
            </div>
            <div class="mkt-detail-panel__seo-row">
              <label>Tags</label>
              <input *ngIf="editing && editDraft" class="mkt-detail-panel__seo-input"
                     [(ngModel)]="editDraft.Tags" placeholder="tag1, tag2, ..." />
              <span *ngIf="!editing">{{ blog.Tags || '—' }}</span>
            </div>
          </div>
        </div>
        </ng-template>
      </div>

      <!-- Revision overlay (floats above action bar) -->
      <div *ngIf="showRevisionBox" class="mkt-detail-panel__revision-overlay">
        <div class="mkt-detail-panel__revision-box">
          <!-- Progress View -->
          <ng-container *ngIf="revisionPhase === 'progress' || revisionPhase === 'complete'; else revisionInputView">
            <div class="mkt-detail-panel__revision-progress" *ngIf="revisionPhase === 'progress'">
              <div class="mkt-detail-panel__revision-progress-icon">
                <i class="fa-solid fa-wand-magic-sparkles"></i>
              </div>
              <div class="mkt-detail-panel__revision-progress-title">Revising your post...</div>
              <div *ngIf="revisionFeedback.trim()" class="mkt-detail-panel__revision-progress-quote">
                "{{ revisionFeedback }}"
              </div>
              <div *ngIf="!revisionFeedback.trim() && selectedCritiqueType !== 'custom'" class="mkt-detail-panel__revision-progress-quote">
                Applying {{ getCritiqueLabel(selectedCritiqueType) }} review
              </div>
              <div class="mkt-detail-panel__revision-progress-timer">{{ revisionElapsed }}s</div>
            </div>
            <div class="mkt-detail-panel__revision-success" *ngIf="revisionPhase === 'complete'">
              <i class="fa-solid fa-circle-check"></i>
              <span>Revision applied</span>
            </div>
          </ng-container>

          <!-- Input View -->
          <ng-template #revisionInputView>
          <div class="mkt-detail-panel__revision-header">
            <label class="mkt-detail-panel__revision-label">
              <i class="fa-solid fa-comment-dots"></i> Revision Instructions
            </label>
            <button class="mkt-detail-panel__revision-close" (click)="cancelRevision()">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <!-- Critique Type Chips -->
          <div class="mkt-detail-panel__critique-chips">
            <button *ngFor="let ct of critiqueTypes"
                    class="mkt-detail-panel__critique-chip"
                    [class.mkt-detail-panel__critique-chip--active]="selectedCritiqueType === ct.key"
                    (click)="selectCritiqueType(ct.key)"
                    [title]="ct.description">
              <i [class]="'fa-solid ' + ct.icon"></i> {{ ct.label }}
            </button>
          </div>

          <textarea class="mkt-detail-panel__revision-input"
                    [(ngModel)]="revisionFeedback"
                    [placeholder]="selectedCritiqueType === 'custom'
                      ? 'Describe what you\\'d like changed (e.g., \\'Make the intro more conversational\\')...'
                      : 'Optional: Add specific notes...'"
                    rows="3"></textarea>
          <label *ngIf="selectedCritiqueType === 'custom'" class="mkt-detail-panel__revision-toggle">
            <input type="checkbox" [(ngModel)]="autoRevise" />
            <i class="fa-solid fa-wand-magic-sparkles"></i> Auto-revise with AI
          </label>
          <div class="mkt-detail-panel__revision-actions">
            <button class="mkt-action-btn mkt-action-btn--save"
                    [disabled]="selectedCritiqueType === 'custom' && !revisionFeedback.trim()"
                    (click)="submitRevision()">
              <i class="fa-solid" [ngClass]="autoRevise ? 'fa-wand-magic-sparkles' : 'fa-comment-dots'"></i>
              {{ autoRevise ? 'Apply Revision' : 'Save Feedback' }}
            </button>
          </div>
          <div *ngIf="revisionError" class="mkt-detail-panel__revision-error">
            <i class="fa-solid fa-triangle-exclamation"></i> {{ revisionError }}
          </div>
          </ng-template>
        </div>
      </div>

      <!-- Sticky Actions -->
      <div class="mkt-detail-panel__actions">
        <ng-container *ngIf="editing; else viewActions">
          <button class="mkt-action-btn mkt-action-btn--save" [disabled]="saving" (click)="saveEdit()">
            <i class="fa-solid" [ngClass]="saving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'"></i>
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
          <button class="mkt-action-btn mkt-action-btn--cancel" [disabled]="saving" (click)="cancelEdit()">
            <i class="fa-solid fa-ban"></i> Cancel
          </button>
        </ng-container>
        <ng-template #viewActions>
          <button class="mkt-action-btn mkt-action-btn--approve"
                  *ngIf="blog.Status === 'Draft' || blog.Status === 'Ready'"
                  (click)="statusChange.emit({ blogId: blog.ID, newStatus: blog.Status === 'Draft' ? 'Ready' : 'Published' })">
            <i class="fa-solid fa-check"></i>
            {{ blog.Status === 'Draft' ? 'Approve' : publishLabel }}
          </button>
          <button class="mkt-action-btn mkt-action-btn--unarchive"
                  *ngIf="blog.Status === 'Archived'"
                  (click)="statusChange.emit({ blogId: blog.ID, newStatus: 'Draft' })">
            <i class="fa-solid fa-rotate-left"></i> Unarchive
          </button>
          <button class="mkt-action-btn mkt-action-btn--revision"
                  *ngIf="!showRevisionBox && (blog.Status === 'Draft' || blog.Status === 'Ready')"
                  (click)="openRevisionBox()">
            <i class="fa-solid fa-comment-dots"></i> Revise
          </button>
          <button class="mkt-action-btn mkt-action-btn--archive"
                  *ngIf="blog.Status !== 'Archived'"
                  (click)="statusChange.emit({ blogId: blog.ID, newStatus: 'Archived' })">
            <i class="fa-solid fa-archive"></i> Archive
          </button>
          <button class="mkt-action-btn mkt-action-btn--delete"
                  (click)="showDeleteConfirm = true">
            <i class="fa-solid fa-trash"></i> Delete
          </button>
        </ng-template>
      </div>
    </div>

    <mkt-confirm-dialog
      [visible]="showDeleteConfirm"
      title="Delete Blog Post"
      message="This will permanently remove this blog post. This action cannot be undone."
      confirmLabel="Delete"
      confirmClass="danger"
      (confirmed)="showDeleteConfirm = false; deleteBlog.emit(blog.ID)"
      (cancelled)="showDeleteConfirm = false">
    </mkt-confirm-dialog>
  `,
  styles: [`
    :host {
      display: block;
      background: var(--mj-bg-surface);
      height: 100%;
      overflow: hidden;
    }

    .mkt-detail-panel {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    /* Header */
    .mkt-detail-panel__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 14px 16px;
      border-bottom: 1px solid var(--mj-border-default);
      flex-shrink: 0;
    }
    .mkt-detail-panel__title {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--mj-text-primary);
      line-height: 1.3;
      flex: 1;
    }
    .mkt-detail-panel__title-input {
      flex: 1;
      margin: 0;
      padding: 4px 8px;
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--mj-text-primary);
      line-height: 1.3;
      border: 1px solid var(--mj-border-default);
      border-radius: 4px;
      outline: none;
      background: var(--mj-bg-surface);
    }
    .mkt-detail-panel__title-input:focus {
      border-color: var(--mj-brand-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-brand-primary) 15%, transparent);
    }
    .mkt-detail-panel__header-actions {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }
    .mkt-detail-panel__edit-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-muted);
      font-size: 0.85rem;
      padding: 2px 6px;
    }
    .mkt-detail-panel__edit-btn:hover {
      color: var(--mj-brand-primary);
    }
    .mkt-detail-panel__history-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-muted);
      font-size: 0.85rem;
      padding: 2px 6px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
    .mkt-detail-panel__history-btn:hover,
    .mkt-detail-panel__history-btn--active {
      color: var(--mj-brand-primary);
    }
    .mkt-detail-panel__history-count {
      font-size: 0.7rem;
      font-weight: 600;
      background: var(--mj-bg-surface-active);
      color: var(--mj-text-primary);
      border-radius: 8px;
      padding: 0 5px;
      min-width: 16px;
      text-align: center;
      line-height: 16px;
    }
    .mkt-detail-panel__history-btn--active .mkt-detail-panel__history-count {
      background: var(--mj-brand-primary);
      color: var(--mj-brand-on-primary);
    }
    .mkt-detail-panel__close {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-muted);
      font-size: 1rem;
      padding: 2px 6px;
      flex-shrink: 0;
    }
    .mkt-detail-panel__close:hover {
      color: var(--mj-text-primary);
    }

    /* Brand Voice Score Banner */
    .mkt-detail-panel__score-banner {
      padding: 10px 16px;
      border-bottom: 1px solid var(--mj-border-default);
    }
    .mkt-detail-panel__score-banner.score-green { background: var(--mj-status-success-bg); }
    .mkt-detail-panel__score-banner.score-yellow { background: var(--mj-status-warning-bg); }
    .mkt-detail-panel__score-banner.score-red { background: var(--mj-status-error-bg); }
    .mkt-detail-panel__score-header {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.85rem;
      font-weight: 600;
    }
    .mkt-detail-panel__score-value { font-size: 1.1rem; }
    .mkt-detail-panel__score-label { font-weight: 400; color: var(--mj-text-secondary); }
    .mkt-detail-panel__score-dimensions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
      margin-top: 8px;
    }
    .mkt-detail-panel__score-dim {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.75rem;
    }
    .mkt-detail-panel__score-dim-name { min-width: 80px; color: var(--mj-text-secondary); }
    .mkt-detail-panel__score-dim-value { min-width: 35px; font-weight: 600; text-align: right; }
    .mkt-detail-panel__score-dim-bar {
      flex: 1;
      height: 4px;
      background: var(--mj-border-default);
      border-radius: 2px;
      overflow: hidden;
    }
    .mkt-detail-panel__score-dim-fill {
      height: 100%;
      background: var(--mj-brand-primary);
      border-radius: 2px;
    }
    .mkt-detail-panel__score-flags {
      margin-top: 8px;
      font-size: 0.75rem;
    }
    .mkt-detail-panel__score-flag {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      padding: 3px 0;
      color: var(--mj-status-warning-text);
    }
    .mkt-detail-panel__score-toggle {
      display: flex;
      align-items: center;
      gap: 4px;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.7rem;
      color: var(--mj-text-muted);
      padding: 4px 0 0;
    }
    .mkt-detail-panel__score-toggle:hover { color: var(--mj-text-primary); }

    /* Body */
    .mkt-detail-panel__body {
      flex: 1;
      overflow-y: auto;
      padding: 14px 16px;
    }

    /* Featured image */
    .mkt-detail-panel__featured-img {
      width: 100%;
      max-height: 240px;
      object-fit: cover;
      border-radius: 8px;
      margin-top: 12px;
    }

    /* Rendered content */
    .mkt-detail-panel__content {
      margin-top: 12px;
      font-size: 0.85rem;
      line-height: 1.6;
      color: var(--mj-text-primary);
    }
    .mkt-detail-panel__content :deep(h2) {
      font-size: 1rem;
      margin: 16px 0 8px;
      color: var(--mj-text-primary);
    }
    .mkt-detail-panel__content :deep(h3) {
      font-size: 0.9rem;
      margin: 12px 0 6px;
      color: var(--mj-text-primary);
    }
    .mkt-detail-panel__content :deep(ul), .mkt-detail-panel__content :deep(ol) {
      padding-left: 20px;
    }
    .mkt-detail-panel__content :deep(table) {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
      font-size: 0.8rem;
    }
    .mkt-detail-panel__content :deep(th), .mkt-detail-panel__content :deep(td) {
      border: 1px solid var(--mj-border-default);
      padding: 6px 10px;
      text-align: left;
    }
    .mkt-detail-panel__content :deep(th) {
      background: var(--mj-bg-page);
      font-weight: 600;
    }
    .mkt-detail-panel__content :deep(strong) {
      color: var(--mj-text-primary);
    }

    /* Content edit textarea */
    .mkt-detail-panel__content-edit {
      margin-top: 12px;
      width: 100%;
      min-height: 300px;
      padding: 8px;
      font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
      font-size: 0.82rem;
      line-height: 1.5;
      color: var(--mj-text-primary);
      background: var(--mj-bg-surface);
      border: 1px solid var(--mj-border-default);
      border-radius: 4px;
      resize: vertical;
      outline: none;
      box-sizing: border-box;
    }
    .mkt-detail-panel__content-edit:focus {
      border-color: var(--mj-brand-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-brand-primary) 15%, transparent);
    }

    /* History view */
    .mkt-detail-panel__history-nav {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
    }
    .mkt-detail-panel__back-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-brand-primary);
      font-size: 0.8rem;
      font-weight: 600;
      padding: 4px 0;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
    .mkt-detail-panel__back-btn:hover { text-decoration: underline; }
    .mkt-detail-panel__history-title {
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--mj-text-primary);
      margin-left: auto;
    }
    .mkt-detail-panel__revision-tag {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--mj-text-muted);
      background: var(--mj-bg-surface-hover);
      padding: 2px 8px;
      border-radius: 4px;
      margin-left: auto;
    }
    .mkt-detail-panel__revision-feedback-display {
      font-size: 0.8rem;
      color: var(--mj-status-warning-text);
      background: var(--mj-status-warning-bg);
      border: 1px solid var(--mj-status-warning-border);
      border-radius: 6px;
      padding: 8px 10px;
      margin-bottom: 12px;
    }
    .mkt-detail-panel__revision-feedback-display i {
      margin-right: 4px;
    }
    .mkt-detail-panel__history-loading {
      font-size: 0.82rem;
      color: var(--mj-text-muted);
      padding: 16px 0;
      text-align: center;
    }
    .mkt-detail-panel__revision-card {
      padding: 10px 12px;
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      margin-bottom: 8px;
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s;
    }
    .mkt-detail-panel__revision-card:hover {
      border-color: var(--mj-brand-primary);
      background: var(--mj-bg-surface-hover);
    }
    .mkt-detail-panel__revision-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .mkt-detail-panel__revision-num {
      font-size: 0.8rem;
      font-weight: 700;
      color: var(--mj-text-primary);
    }
    .mkt-detail-panel__revision-date {
      font-size: 0.72rem;
      color: var(--mj-text-disabled);
    }
    .mkt-detail-panel__revision-card-title {
      font-size: 0.82rem;
      color: var(--mj-text-primary);
      margin-top: 4px;
      font-weight: 500;
    }
    .mkt-detail-panel__revision-card-feedback {
      font-size: 0.75rem;
      color: var(--mj-status-warning-text);
      margin-top: 4px;
    }
    .mkt-detail-panel__revision-card-feedback i {
      margin-right: 3px;
    }

    /* Sections */
    .mkt-detail-panel__section {
      margin-top: 16px;
      padding-top: 14px;
      border-top: 1px solid var(--mj-bg-surface-hover);
    }
    .mkt-detail-panel__section-title {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 0 0 8px;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--mj-text-secondary);
      cursor: pointer;
      user-select: none;
    }
    .mkt-detail-panel__section-title i:last-child {
      margin-left: auto;
      font-size: 0.7rem;
      color: var(--mj-text-disabled);
    }

    /* SEO */
    .mkt-detail-panel__seo {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .mkt-detail-panel__seo-row {
      font-size: 0.8rem;
    }
    .mkt-detail-panel__seo-row label {
      display: block;
      font-weight: 600;
      color: var(--mj-text-secondary);
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      margin-bottom: 2px;
    }
    .mkt-detail-panel__seo-row span {
      color: var(--mj-text-secondary);
      line-height: 1.4;
    }
    .mkt-detail-panel__seo-input {
      width: 100%;
      padding: 4px 8px;
      font-size: 0.8rem;
      border: 1px solid var(--mj-border-default);
      border-radius: 4px;
      outline: none;
      box-sizing: border-box;
      background: var(--mj-bg-surface);
      color: var(--mj-text-primary);
    }
    .mkt-detail-panel__seo-input:focus {
      border-color: var(--mj-brand-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-brand-primary) 15%, transparent);
    }
    .mkt-detail-panel__seo-textarea {
      width: 100%;
      min-height: 60px;
      padding: 4px 8px;
      font-size: 0.8rem;
      border: 1px solid var(--mj-border-default);
      border-radius: 4px;
      resize: vertical;
      outline: none;
      box-sizing: border-box;
      background: var(--mj-bg-surface);
      color: var(--mj-text-primary);
    }
    .mkt-detail-panel__seo-textarea:focus {
      border-color: var(--mj-brand-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-brand-primary) 15%, transparent);
    }

    /* Sticky Actions */
    .mkt-detail-panel__actions {
      display: flex;
      gap: 8px;
      padding: 12px 16px;
      border-top: 1px solid var(--mj-border-default);
      background: var(--mj-bg-surface);
      flex-shrink: 0;
    }
    .mkt-action-btn {
      flex: 1;
      padding: 8px 12px;
      border: none;
      border-radius: 6px;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: opacity 0.15s;
    }
    .mkt-action-btn:hover { opacity: 0.9; }
    .mkt-action-btn:active:not(:disabled) { transform: scale(0.97); }
    .mkt-action-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .mkt-action-btn--save {
      background: var(--mj-brand-primary);
      color: var(--mj-brand-on-primary);
    }
    .mkt-action-btn--cancel {
      background: var(--mj-bg-surface-active);
      color: var(--mj-text-primary);
    }
    .mkt-action-btn--approve {
      background: var(--mj-status-success);
      color: var(--mj-text-inverse);
    }
    .mkt-action-btn--unarchive {
      background: var(--mj-status-info);
      color: var(--mj-text-inverse);
    }
    .mkt-action-btn--archive {
      background: var(--mj-bg-surface-active);
      color: var(--mj-text-primary);
    }
    .mkt-action-btn--delete {
      background: var(--mj-status-error-bg);
      color: var(--mj-status-error);
    }
    .mkt-action-btn--revision {
      background: var(--mj-status-warning-bg);
      color: var(--mj-status-warning-text);
    }

    /* Revision overlay */
    .mkt-detail-panel__revision-overlay {
      flex-shrink: 0;
      border-top: 1px solid var(--mj-border-default);
      background: var(--mj-bg-surface);
      animation: mktRevisionSlideUp 0.2s ease;
    }
    @keyframes mktRevisionSlideUp {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .mkt-detail-panel__revision-box {
      padding: 12px 16px;
      background: var(--mj-status-warning-bg);
      border-bottom: 1px solid var(--mj-status-warning-border);
    }
    .mkt-detail-panel__revision-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }
    .mkt-detail-panel__revision-header .mkt-detail-panel__revision-label {
      margin-bottom: 0;
    }
    .mkt-detail-panel__revision-close {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-muted);
      font-size: 0.9rem;
      padding: 2px 6px;
      border-radius: 4px;
      transition: all 0.15s;
    }
    .mkt-detail-panel__revision-close:hover {
      color: var(--mj-text-primary);
      background: var(--mj-bg-surface-hover);
    }

    /* Critique chips */
    .mkt-detail-panel__critique-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 10px;
    }
    .mkt-detail-panel__critique-chip {
      padding: 4px 10px;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid var(--mj-border-default);
      border-radius: 16px;
      background: var(--mj-bg-surface);
      color: var(--mj-text-muted);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      transition: all 0.15s;
    }
    .mkt-detail-panel__critique-chip:hover {
      border-color: var(--mj-status-warning);
      color: var(--mj-status-warning-text);
    }
    .mkt-detail-panel__critique-chip--active {
      background: var(--mj-status-warning);
      border-color: var(--mj-status-warning);
      color: var(--mj-text-inverse);
    }
    .mkt-detail-panel__critique-chip--active:hover {
      background: var(--mj-color-warning-600);
      border-color: var(--mj-color-warning-600);
      color: var(--mj-text-inverse);
    }

    /* Critique badge in revision history */
    .mkt-detail-panel__critique-badge {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 1px 7px;
      font-size: 0.68rem;
      font-weight: 600;
      color: var(--mj-status-warning-text);
      background: var(--mj-status-warning-bg);
      border: 1px solid var(--mj-status-warning-border);
      border-radius: 10px;
      margin-right: 4px;
      white-space: nowrap;
    }
    .mkt-detail-panel__critique-badge i {
      font-size: 0.62rem;
    }

    /* Progress view */
    .mkt-detail-panel__revision-progress {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 16px 8px;
      text-align: center;
    }
    .mkt-detail-panel__revision-progress-icon {
      font-size: 1.5rem;
      color: var(--mj-status-warning);
      animation: mktPulseWand 1.5s ease-in-out infinite;
    }
    .mkt-detail-panel__revision-progress-title {
      font-size: 0.88rem;
      font-weight: 600;
      color: var(--mj-status-warning-text);
      margin-top: 8px;
    }
    .mkt-detail-panel__revision-progress-quote {
      font-size: 0.78rem;
      color: var(--mj-text-muted);
      font-style: italic;
      margin-top: 6px;
      max-width: 280px;
      line-height: 1.4;
    }
    .mkt-detail-panel__revision-progress-timer {
      font-size: 0.82rem;
      font-weight: 600;
      color: var(--mj-status-warning-text);
      margin-top: 8px;
      font-variant-numeric: tabular-nums;
    }
    @keyframes mktPulseWand {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.6; transform: scale(1.1); }
    }

    /* Success flash */
    .mkt-detail-panel__revision-success {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 16px 8px;
      font-size: 0.88rem;
      font-weight: 600;
      color: var(--mj-status-success-text);
    }
    .mkt-detail-panel__revision-success i {
      font-size: 1.2rem;
    }
    .mkt-detail-panel__revision-label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--mj-status-warning-text);
      margin-bottom: 8px;
    }
    .mkt-detail-panel__revision-input {
      width: 100%;
      padding: 8px;
      font-size: 0.82rem;
      line-height: 1.5;
      color: var(--mj-text-primary);
      border: 1px solid var(--mj-status-warning-border);
      border-radius: 4px;
      resize: vertical;
      outline: none;
      box-sizing: border-box;
      background: var(--mj-bg-surface);
    }
    .mkt-detail-panel__revision-input:focus {
      border-color: var(--mj-status-warning);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-status-warning) 15%, transparent);
    }
    .mkt-detail-panel__revision-toggle {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 8px;
      font-size: 0.8rem;
      color: var(--mj-text-primary);
      cursor: pointer;
      user-select: none;
    }
    .mkt-detail-panel__revision-toggle input[type="checkbox"] {
      margin: 0;
      cursor: pointer;
    }
    .mkt-detail-panel__revision-toggle i {
      color: var(--mj-status-warning);
      font-size: 0.75rem;
    }
    .mkt-detail-panel__revision-actions {
      display: flex;
      gap: 8px;
      margin-top: 8px;
    }
    .mkt-detail-panel__revision-error {
      margin-top: 8px;
      padding: 6px 10px;
      font-size: 0.78rem;
      color: var(--mj-status-error-text);
      background: var(--mj-status-error-bg);
      border-radius: 4px;
    }
  `]
})
export class BlogDetailPanelComponent implements OnChanges, OnDestroy {
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  /**
   * See ContentCardComponent.publishLabel. This panel has four hosts and only ONE of them
   * pushes: cmo-dashboard routes the Ready button through the Publish To CMS action and so
   * passes 'Push to HubSpot'. marketing-dashboard-v2, run-detail and review-queue only
   * write a status column, so the default has to stay honest for them.
   */
  @Input() publishLabel: string = 'Mark Published';
  @Input() blog!: Blog;
  @Input() allRevisions: BlogRevision[] = [];
  @Input() brandVoiceScore: BrandVoiceScoreDisplay | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() statusChange = new EventEmitter<{ blogId: string; newStatus: string }>();
  @Output() deleteBlog = new EventEmitter<string>();
  @Output() blogSaved = new EventEmitter<string>();
  @Output() revisionRequested = new EventEmitter<{ blogId: string; feedback: string; feedbackOnly: boolean; critiqueType: string }>();

  private sanitizer = inject(DomSanitizer);

  renderedContent: string = '';
  showSeo: boolean = false;
  showDeleteConfirm: boolean = false;

  editing = false;
  saving = false;
  editDraft: EditDraft | null = null;

  showRevisionBox = false;
  revising = false;
  revisionFeedback = '';
  revisionError = '';
  autoRevise = true;

  // Revision progress state
  revisionPhase: 'input' | 'progress' | 'complete' = 'input';
  revisionElapsed = 0;
  private revisionStartTime = 0;
  private revisionTimer: any = null;

  // Critique types
  selectedCritiqueType = 'custom';
  critiqueTypes: CritiqueType[] = [
    { key: 'custom', label: 'Custom', icon: 'fa-pen', description: 'Free-form revision feedback', requiresFeedback: true },
    { key: 'brand', label: 'Brand Voice', icon: 'fa-shield-halved', description: 'Review through Brand Guardian lens', requiresFeedback: false },
    { key: 'seo', label: 'SEO', icon: 'fa-magnifying-glass-chart', description: 'Review through SEO/AIEO specialist lens', requiresFeedback: false },
    { key: 'editorial', label: 'Editorial', icon: 'fa-spell-check', description: 'Review through senior editor lens', requiresFeedback: false },
  ];

  // Brand voice score display
  showScoreDetails = false;

  get scoreDimensions(): Array<{ key: string; label: string; score: number }> {
    if (!this.brandVoiceScore?.dimensions) return [];
    const labels: Record<string, string> = {
      toneAlignment: 'Tone Alignment',
      vocabularyConsistency: 'Vocabulary',
      readabilityAndStructure: 'Readability',
      audienceFit: 'Audience Fit',
      valueFocus: 'Value Focus',
      authenticity: 'Authenticity',
    };
    return Object.entries(this.brandVoiceScore.dimensions).map(([key, dim]) => ({
      key,
      label: labels[key] || key,
      score: dim.score,
    }));
  }

  // History state
  showHistory = false;
  revisions: BlogRevision[] = [];
  selectedRevision: BlogRevision | null = null;
  selectedRevisionContent = '';
  revisionCount = 0;
  loadingHistory = false;

  ngOnDestroy(): void {
    if (this.revisionTimer) {
      clearInterval(this.revisionTimer);
      this.revisionTimer = null;
    }
  }

  ngOnChanges(): void {
    if (this.blog?.Content) {
      this.renderedContent = this.sanitizer.sanitize(SecurityContext.HTML, marked.parse(this.blog.Content) as string) || '';
    }
    this.showHistory = false;
    this.selectedRevision = null;
    // Filter pre-loaded revisions for this blog — instant, no async
    this.revisions = this.allRevisions.filter(r => r.BlogID === this.blog?.ID);
    this.revisionCount = this.revisions.length;
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    this.selectedRevision = null;
    // revisions already populated from ngOnChanges — nothing to load
  }

  viewRevision(rev: BlogRevision): void {
    this.selectedRevision = rev;
    this.selectedRevisionContent = this.sanitizer.sanitize(SecurityContext.HTML, marked.parse(rev.Content || '*No content*') as string) || '';
  }

  enterEdit(): void {
    this.editDraft = {
      Title: this.blog.Title || '',
      Content: this.blog.Content || '',
      Author: this.blog.Author || '',
      Tags: this.blog.Tags || '',
      URLSlug: this.blog.URLSlug || '',
      MetaDescription: this.blog.MetaDescription || '',
    };
    this.editing = true;
    this.showSeo = true;
  }

  cancelEdit(): void {
    this.editing = false;
    this.editDraft = null;
  }

  async saveEdit(): Promise<void> {
    if (!this.editDraft || this.saving) return;
    this.saving = true;

    this.blog.Title = this.editDraft.Title;
    this.blog.Content = this.editDraft.Content;
    this.blog.Author = this.editDraft.Author || null;
    this.blog.Tags = this.editDraft.Tags || null;
    this.blog.URLSlug = this.editDraft.URLSlug || null;
    this.blog.MetaDescription = this.editDraft.MetaDescription || null;

    const saved = await this.blog.Save();
    this.saving = false;

    if (saved) {
      this.renderedContent = this.sanitizer.sanitize(SecurityContext.HTML, marked.parse(this.blog.Content || '') as string) || '';
      this.editing = false;
      this.editDraft = null;
      this.blogSaved.emit(this.blog.ID);
    } else {
      const lastResult = this.blog.ResultHistory?.length
        ? this.blog.ResultHistory[this.blog.ResultHistory.length - 1]
        : null;
      const errorMsg = lastResult?.Message || 'Unknown error';
      alert('Unable to save blog post: ' + errorMsg);
    }
  }

  openRevisionBox(): void {
    this.showRevisionBox = true;
    this.revisionFeedback = '';
    this.revisionError = '';
    this.autoRevise = true;
    this.revisionPhase = 'input';
    this.selectedCritiqueType = 'custom';
  }

  cancelRevision(): void {
    this.stopRevisionTimer();
    this.showRevisionBox = false;
    this.revisionFeedback = '';
    this.revisionError = '';
    this.autoRevise = true;
    this.revisionPhase = 'input';
    this.selectedCritiqueType = 'custom';
  }

  selectCritiqueType(key: string): void {
    this.selectedCritiqueType = key;
    const ct = this.critiqueTypes.find(c => c.key === key);
    if (ct && !ct.requiresFeedback) {
      this.autoRevise = true;
    }
  }

  getCritiqueLabel(key: string): string {
    return this.critiqueTypes.find(c => c.key === key)?.label || key;
  }

  private static readonly critiqueBadgeMap: Record<string, { label: string; icon: string }> = {
    'Brand Voice Review': { label: 'Brand Voice', icon: 'fa-shield-halved' },
    'SEO Review': { label: 'SEO', icon: 'fa-magnifying-glass-chart' },
    'Editorial Review': { label: 'Editorial', icon: 'fa-spell-check' },
  };

  getCritiqueBadge(feedback: string): { label: string; icon: string } | null {
    if (!feedback?.startsWith('[')) return null;
    const end = feedback.indexOf(']');
    if (end < 0) return null;
    const tag = feedback.substring(1, end);
    return BlogDetailPanelComponent.critiqueBadgeMap[tag] || null;
  }

  getFeedbackText(feedback: string): string {
    if (!feedback?.startsWith('[')) return feedback;
    const end = feedback.indexOf(']');
    if (end < 0) return feedback;
    return feedback.substring(end + 1).trim();
  }

  submitRevision(): void {
    if (this.revising) return;
    const ct = this.critiqueTypes.find(c => c.key === this.selectedCritiqueType);
    // Custom requires feedback; specialist modes don't
    if (ct?.requiresFeedback && !this.revisionFeedback.trim()) return;

    this.revising = true;
    this.revisionError = '';

    // Start progress view if auto-revising
    if (this.autoRevise) {
      this.revisionPhase = 'progress';
      this.revisionElapsed = 0;
      this.revisionStartTime = Date.now();
      this.startRevisionTimer();
    }

    this.revisionRequested.emit({
      blogId: this.blog.ID,
      feedback: this.revisionFeedback,
      feedbackOnly: !this.autoRevise,
      critiqueType: this.selectedCritiqueType,
    });
  }

  async onRevisionComplete(success: boolean, error?: string): Promise<void> {
    this.stopRevisionTimer();
    this.revising = false;

    if (success) {
      if (this.revisionPhase === 'progress') {
        // Show success flash briefly
        this.revisionPhase = 'complete';
        this.cdr.detectChanges();
        // Reload blog data while the flash is visible
        await this.blog.Load(this.blog.ID);
        this.renderedContent = this.sanitizer.sanitize(SecurityContext.HTML, marked.parse(this.blog.Content || '') as string) || '';
        this.refreshRevisions();
        setTimeout(() => {
          this.zone.run(() => {
            this.showRevisionBox = false;
            this.revisionFeedback = '';
            this.revisionPhase = 'input';
            this.selectedCritiqueType = 'custom';
            this.cdr.detectChanges();
          });
        }, 600);
      } else {
        // Feedback-only mode — close immediately
        this.showRevisionBox = false;
        this.revisionFeedback = '';
        this.revisionPhase = 'input';
        this.selectedCritiqueType = 'custom';
        this.refreshRevisions();
        this.cdr.detectChanges();
      }
    } else {
      this.revisionPhase = 'input';
      this.revisionError = error || 'Revision failed. Please try again.';
      this.cdr.detectChanges();
    }
  }

  private startRevisionTimer(): void {
    this.stopRevisionTimer();
    this.revisionTimer = setInterval(() => {
      this.zone.run(() => {
        this.revisionElapsed = Math.floor((Date.now() - this.revisionStartTime) / 1000);
        this.cdr.detectChanges();
      });
    }, 1000);
  }

  private stopRevisionTimer(): void {
    if (this.revisionTimer) {
      clearInterval(this.revisionTimer);
      this.revisionTimer = null;
    }
  }

  private async refreshRevisions(): Promise<void> {
    try {
      const rv = new RunView();
      const result = await rv.RunView({
        EntityName: 'Blog Revisions',
        ExtraFilter: `BlogID='${this.blog.ID}'`,
        OrderBy: 'RevisionNumber DESC',
        ResultType: 'simple'
      });
      if (result.Success) {
        this.revisions = result.Results as BlogRevision[];
        this.revisionCount = this.revisions.length;
      }
    } catch { /* keep existing data */ }
  }
}
